import { PaymentConfigSection } from "@/apidtos";
import { useRootLoaderData } from "@/root";

/**
 * Hook to access payment configuration from PxConfig.
 * This includes enabled payment providers, feature flags, and other payment settings.
 * 
 * The EnabledProviders array contains keys of active payment providers from the
 * PaymentMethodProvider table (where IsActive = true).
 * 
 * @example
 * ```tsx
 * const paymentsConfig = usePaymentsConfig();
 * if (paymentsConfig.EnabledProviders?.includes("Stripe")) {
 *   // Show Stripe-specific UI
 * }
 * ```
 */
export const usePaymentsConfig = () => {
  const { config } = useRootLoaderData();
  return config.Payments as PaymentConfigSection;
}

/**
 * Check if a specific payment provider is enabled.
 * 
 * @param providerKey - The provider key (e.g., "Stripe", "PayPal")
 * @returns True if the provider is active in the PaymentMethodProvider table
 * 
 * @example
 * ```tsx
 * const isStripeEnabled = useIsPaymentProviderEnabled("Stripe");
 * if (isStripeEnabled) {
 *   // Render Stripe components
 * }
 * ```
 */
export function useIsPaymentProviderEnabled(providerKey: string): boolean {
  const config = usePaymentsConfig();
  return config.EnabledProviders?.includes(providerKey) ?? false;
}

